import { useEffect, useState, useCallback } from 'react';

interface ConfettiPiece {
  id: number;
  x: number;
  y: number;
  rotation: number;
  color: string;
  size: number;
  speedX: number;
  speedY: number;
  rotationSpeed: number;
  shape: 'square' | 'circle' | 'triangle';
}

interface ConfettiProps {
  active: boolean;
  onComplete?: () => void;
  duration?: number;
  particleCount?: number;
}

// Confetti colors - mix of brand and celebratory colors
// Note: This is decorative, so some non-brand colors are acceptable
const COLORS = [
  '#2499D5', // Halton Blue (brand)
  '#212d62', // Deep Blue (brand)
  '#24d5c5', // Cool Green (brand)
  '#44bc63', // Green (brand)
  '#F7941D', // Orange (utility - for celebration)
  '#FFD700', // Gold (celebratory accent)
  '#9333ea', // Purple (celebratory accent)
  '#ec4899', // Pink (celebratory accent)
];

export const Confetti = ({ 
  active, 
  onComplete, 
  duration = 3000,
  particleCount = 100 
}: ConfettiProps) => {
  const [pieces, setPieces] = useState<ConfettiPiece[]>([]);
  const [isAnimating, setIsAnimating] = useState(false);

  const createPieces = useCallback(() => {
    const newPieces: ConfettiPiece[] = [];
    const shapes: ('square' | 'circle' | 'triangle')[] = ['square', 'circle', 'triangle'];
    
    for (let i = 0; i < particleCount; i++) {
      newPieces.push({
        id: i,
        x: Math.random() * 100,
        y: -10 - Math.random() * 20,
        rotation: Math.random() * 360,
        color: COLORS[Math.floor(Math.random() * COLORS.length)],
        size: 6 + Math.random() * 8,
        speedX: (Math.random() - 0.5) * 3,
        speedY: 2 + Math.random() * 3,
        rotationSpeed: (Math.random() - 0.5) * 10,
        shape: shapes[Math.floor(Math.random() * shapes.length)],
      });
    }
    return newPieces;
  }, [particleCount]);

  useEffect(() => {
    if (active && !isAnimating) {
      setIsAnimating(true);
      setPieces(createPieces());
      
      const timer = setTimeout(() => {
        setIsAnimating(false);
        setPieces([]);
        onComplete?.();
      }, duration);

      return () => clearTimeout(timer);
    }
  }, [active, isAnimating, createPieces, duration, onComplete]);

  useEffect(() => {
    if (!isAnimating || pieces.length === 0) return;

    const interval = setInterval(() => {
      setPieces(prevPieces => 
        prevPieces.map(piece => ({
          ...piece,
          x: piece.x + piece.speedX,
          y: piece.y + piece.speedY,
          rotation: piece.rotation + piece.rotationSpeed,
          speedY: piece.speedY + 0.1, // gravity
        })).filter(piece => piece.y < 120)
      );
    }, 16);

    return () => clearInterval(interval);
  }, [isAnimating, pieces.length]);

  if (!isAnimating || pieces.length === 0) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        pointerEvents: 'none',
        zIndex: 9999,
        overflow: 'hidden',
      }}
    >
      {pieces.map(piece => (
        <div
          key={piece.id}
          style={{
            position: 'absolute',
            left: `${piece.x}%`,
            top: `${piece.y}%`,
            width: piece.size,
            height: piece.size,
            backgroundColor: piece.shape !== 'triangle' ? piece.color : 'transparent',
            borderRadius: piece.shape === 'circle' ? '50%' : '0',
            borderLeft: piece.shape === 'triangle' ? `${piece.size / 2}px solid transparent` : undefined,
            borderRight: piece.shape === 'triangle' ? `${piece.size / 2}px solid transparent` : undefined,
            borderBottom: piece.shape === 'triangle' ? `${piece.size}px solid ${piece.color}` : undefined,
            transform: `rotate(${piece.rotation}deg)`,
            opacity: 0.9,
          }}
        />
      ))}
    </div>
  );
};

// Hook for easy confetti triggering
export const useConfetti = () => {
  const [showConfetti, setShowConfetti] = useState(false);

  const triggerConfetti = useCallback(() => {
    setShowConfetti(true);
  }, []);

  const handleComplete = useCallback(() => {
    setShowConfetti(false);
  }, []);

  return {
    showConfetti,
    triggerConfetti,
    handleComplete,
  };
};

export default Confetti;
